"use client";

import type { ImpactPartner } from "@mw/shared/payload-types";

import { useTranslations } from "next-intl";
import { useCallback, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { getUrlDisplayName } from "@utilities/url";

export type CategoryName = ImpactPartner["category"] | "all";

export const useGetCategoryLabel = () => {
  const t = useTranslations("ClientKeys.Components.impactGrid");
  return useCallback(
    (category: CategoryName) => {
      switch (category) {
        case "healthcare":
          return t("category_healthcare");
        case "education":
          return t("category_education");
        case "culture":
          return t("category_culture");
        case "all":
          return t("category_all");
        default:
          const _exhaustiveCheck: never = category;
          throw new Error(`Unknown impact partner category: ${category}`);
      }
    },
    [t],
  );
};

const normalizeSearchValue = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-_/.,]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const impactPartnerMatchesSearch = ({
  partner,
  query,
  categoryLabel,
  applicationLabel,
}: {
  partner: ImpactPartner;
  query: string;
  categoryLabel: string;
  applicationLabel: string;
}) => {
  const normalizedQuery = normalizeSearchValue(query);

  if (!normalizedQuery) {
    return true;
  }

  const singularQuery =
    normalizedQuery.length > 4 && normalizedQuery.endsWith("s") && !normalizedQuery.endsWith("ss")
      ? normalizedQuery.slice(0, -1)
      : normalizedQuery;
  const searchTerms = singularQuery === normalizedQuery ? [normalizedQuery] : [normalizedQuery, singularQuery];
  const useCaseFields =
    partner.useCases?.flatMap(useCase => [
      useCase.title,
      useCase.link?.label,
      useCase.link?.ariaLabel,
      useCase.link?.url,
    ]) ?? [];
  const searchText = normalizeSearchValue(
    [
      partner.name,
      partner.description,
      partner.category,
      categoryLabel,
      partner.type,
      applicationLabel,
      partner.diagnosisType,
      partner.website,
      getUrlDisplayName(partner.website),
      ...useCaseFields,
    ]
      .filter(field => field?.trim())
      .join(" "),
  );

  return searchTerms.some(term => searchText.includes(term));
};

export const useImpactPartnerSearch = (partners: ImpactPartner[]) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useDebounceValue(searchQuery, 300);
  const getCategoryLabel = useGetCategoryLabel();
  const t = useTranslations("ClientKeys.Components.impactGrid");
  const applicationPartnerLabel = t("application_partner");
  const applicationNoneLabel = t("application_none");

  const handleSearchQueryChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setDebouncedSearchQuery(value);
    },
    [setSearchQuery, setDebouncedSearchQuery],
  );

  const filteredPartners = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return partners;
    }

    return partners.filter(partner =>
      impactPartnerMatchesSearch({
        partner,
        query: debouncedSearchQuery,
        categoryLabel: getCategoryLabel(partner.category),
        applicationLabel: partner.type === "application" ? applicationPartnerLabel : applicationNoneLabel,
      }),
    );
  }, [partners, debouncedSearchQuery, getCategoryLabel, applicationPartnerLabel, applicationNoneLabel]);

  const partnersByCategory = useMemo(() => {
    const grouped: Partial<Record<ImpactPartner["category"], ImpactPartner[]>> = {};

    filteredPartners.forEach(partner => (grouped[partner.category] ??= []).push(partner));

    return grouped;
  }, [filteredPartners]);

  return {
    searchQuery,
    setSearchQuery: handleSearchQueryChange,
    partnersByCategory,
  };
};
